import React from 'react'

import { Button, Modal } from '@octane/spark'
import { editUserPermissions, sendResetPassword } from 'api/user'
import { AxiosError } from 'axios'
import { UserBasicInfoForm } from 'components/UserForm'
import { Form, Formik } from 'formik'
import useAppDispatch from 'hooks/useAppDispatch'
import useUser from 'hooks/useUser'
import { loadUsersAndSalespersons } from 'state/dealership/actions'
import { LEVEL_OF_ACCESS_REGIONAL_MANAGER } from 'utils/consts/securityOptions'
import * as toast from 'utils/toast'

import {
    Divisor,
    FormTitle,
    LinkButton,
    PasswordResetEmailButton,
} from '../UserForm/UserForm.styled'
import { UserPermissionsSchema } from './schema.yup'
import { UserPermissions, UserPermissionsModalProps } from './types'

const INITIAL_SESSION_INACTIVITY_MINUTES = '30'
const INITIAL_PASSWORD_ROTATION_DAY = '90'

export default function UserPermissionsModal({
    onClose,
    user,
}: Readonly<UserPermissionsModalProps>) {
    const initialValues: UserPermissions = {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        emailAddress: user?.emailAddress ?? '',
        levelOfAccess: user?.levelOfAccess ?? '',
        applicationAccess: user?.applicationAccess ?? '',
        sessionInactivity: INITIAL_SESSION_INACTIVITY_MINUTES,
        passwordRotation: INITIAL_PASSWORD_ROTATION_DAY,
        useMfa: 'No',
        disabledFields:
            user?.levelOfAccess === 'Sales'
                ? []
                : ['firstName', 'lastName', 'emailAddress'],
        phoneNumber: user?.phoneNumber ?? '',
    }

    const dispatch = useAppDispatch()
    const { user: currentUser } = useUser()

    const canSendPasswordReset =
        currentUser?.levelOfAccess === LEVEL_OF_ACCESS_REGIONAL_MANAGER

    function handleEditConfirm(userPermissions: UserPermissions) {
        if (user)
            editUserPermissions(user.id, userPermissions)
                .then((response: { detail: string }) => {
                    toast.success({
                        message: response.detail || '',
                    })

                    dispatch(loadUsersAndSalespersons())
                })
                .catch((error: AxiosError) => {
                    toast.error({
                        message: error.message || '',
                    })
                })
                .finally(() => {
                    onClose()
                })
    }

    function handleSendPasswordReset() {
        if (user?.emailAddress) {
            sendResetPassword(user.emailAddress)
                .then(() => {
                    toast.success({
                        message: 'Password reset email sent successfully',
                    })
                })
                .catch((error: AxiosError) => {
                    toast.error({
                        message:
                            error.message ||
                            'Failed to send password reset email',
                    })
                })
        }
    }

    return (
        <Modal isOpen onClose={onClose} size="large">
            <Modal.Body>
                <FormTitle>Edit User&apos;s Permissions</FormTitle>
                <Formik
                    initialValues={initialValues}
                    validationSchema={UserPermissionsSchema}
                    onSubmit={(e) => handleEditConfirm(e)}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <UserBasicInfoForm selectedUser={user} />
                            <Divisor />
                            <Button
                                fullWidth
                                loading={isSubmitting}
                                type="submit"
                            >
                                Save Updated Permissions
                            </Button>
                            {canSendPasswordReset && (
                                <PasswordResetEmailButton
                                    fullWidth
                                    variant="primaryOutlined"
                                    onClick={handleSendPasswordReset}
                                    style={{ marginTop: '6px' }}
                                >
                                    Send Password Reset Email
                                </PasswordResetEmailButton>
                            )}
                            <LinkButton
                                fullWidth
                                variant="white"
                                onClick={onClose}
                                style={{ marginTop: '6px' }}
                            >
                                Cancel
                            </LinkButton>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    )
}
